import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { usePopupHome, pageTabName, popupHomeSlot } from '../../services/popupHome';   // one tab only (report c2846304)
import { tabColor } from '../../tabPalette';

// ── "Your batch report is ready" (Calem, Bugify #044e76b0) ───────────────────────────────────────
// "…create a full-web browser report that pops up on the user's screen, letting them know that it is
// ready (finished) once all bills, invoices, and receipts have been analyzed."
//
// The staff member is in Telegram when they tap ✅ Done, and the bot hands them the link there too.
// This is the other half of that: whichever SLF tab they come back to says the report is waiting and
// opens it. Shown in ONE tab only (services/popupHome: the Tax page's tab if one is open, else the
// tab in front) — a card in all six open tabs is the very thing report c2846304 was about — and the
// report itself opens in one named tab, so pressing this twice brings that tab forward instead of
// stacking a second copy of it.
//
// It carries no × on purpose: it stands for a pile of documents somebody still has to check, and it
// comes down when the report is opened (the server marks the batch read) — exactly like the Filing
// and Active Jobs cards it sits in the corner beside.
const POLL_MS = 120_000;
const FIRST_MS = 4_000;
const HOME = 'tax';   // the page this belongs on: the batch is tax lines

type Pending = { id: string; at: number; count: number; needs: number; client: string };

export default function TaxBatchNotice() {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  const mine = !!me && (me.modules || []).includes('tax');
  const [pending, setPending] = useState<Pending | null>(null);
  const opened = useRef<Set<string>>(new Set());

  const poll = useCallback(async () => {
    if (!localStorage.getItem('mf_token')) return;
    try {
      const d: any = await api.taxBatchPending();
      const b = d?.batch && d.batch.id ? d.batch as Pending : null;
      // Opened from here already: the server takes it off its own list the moment the report loads,
      // but this tab need not wait for the next look to stop offering it.
      setPending(b && !opened.current.has(b.id) ? b : null);
    } catch { /* transient — the next look brings it back */ }
  }, []);
  useEffect(() => {
    if (!mine) { setPending(null); return; }
    const first = setTimeout(() => { void poll(); }, FIRST_MS);
    const id = setInterval(() => { void poll(); }, POLL_MS);
    return () => { clearTimeout(first); clearInterval(id); };
  }, [mine, poll]);
  // …and the moment they come back to this tab, which is usually straight from the chat.
  useEffect(() => {
    if (!mine) return;
    const onVisible = () => { if (document.visibilityState === 'visible') void poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mine, poll]);

  const home = usePopupHome(HOME, !!pending);
  if (!mine || !pending || home === 'hidden') return null;

  const accent = tabColor(HOME) || 'var(--accent)';
  const open = () => {
    opened.current.add(pending.id);
    setPending(null);
    window.open(`/m/tax_batch/${pending.id}`, pageTabName('tax_batch'));
  };

  // Dressed as the "open the page this question belongs on" card (components/PopupHomeNotice), in the
  // Tax page's own top-menu colour, in the same corner and under the same header rows.
  return (
    <div aria-live="polite" style={{ position: 'fixed', right: 24, top: 76 + popupHomeSlot(HOME) * 96, zIndex: 4600, width: 320, maxWidth: 'calc(100vw - 48px)', pointerEvents: 'none' }}>
      <button onClick={open} title={t('txb_notice_open')}
        style={{ pointerEvents: 'auto', width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${accent}`, borderRadius: 12, boxShadow: 'var(--shadow-md)', animation: 'mfMenuIn 0.22s ease', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
        <span style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--c-gray-100)', color: accent }}>
          <i className="ti ti-report-analytics" style={{ fontSize: 16 }} aria-hidden />
        </span>
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('txb_notice_title')}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pending.client || t('txb_notice_open')}</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)', wordBreak: 'break-word' }}>
            {t('txb_notice_body', { n: pending.count })}
            {pending.needs > 0 && ` ${t('txb_needs', { n: pending.needs })}`}
          </span>
        </span>
      </button>
    </div>
  );
}
